/**
 * Paper-check, room-scan and answer-sheet photos, grouped by kind, with a
 * lightbox. Room scans and paper checks are taken before the timer starts, so
 * their offsets are negative — the gallery keeps them in that order rather than
 * clamping them to zero.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { AttemptMediaKind } from "@exam/shared";
import { mediaKindIsVideo } from "@exam/shared";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatClock, formatTime } from "../lib/format";
import type { PhotoItem } from "../data/types";

const KIND_LABEL: Record<AttemptMediaKind, string> = {
  room_scan: "Room scan",
  paper_check: "Paper check",
  answer_sheet: "Answer sheet",
};

const KIND_ORDER: AttemptMediaKind[] = ["room_scan", "paper_check", "answer_sheet"];

/** Signed offsets read as "1:20 before the exam started". */
function offsetLabel(offsetMs: number): string {
  if (offsetMs < 0) return `${formatClock(-offsetMs)} before start`;
  return formatClock(offsetMs);
}

export function PhotoGallery({
  photos,
  onSeek,
}: {
  photos: PhotoItem[];
  onSeek?: (ms: number) => void;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const step = useCallback(
    (delta: number) =>
      setOpenIndex((current) => {
        if (current === null) return current;
        const next = current + delta;
        if (next < 0 || next >= photos.length) return current;
        return next;
      }),
    [photos.length],
  );

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenIndex(null);
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex, step]);

  if (photos.length === 0) {
    return <p className="px-4 py-6 text-xs text-ink-faint">No photos were uploaded.</p>;
  }

  const open = openIndex === null ? null : photos[openIndex];

  return (
    <>
      <div className="space-y-4 p-4">
        {KIND_ORDER.map((kind) => {
          const group = photos.filter((p) => p.kind === kind);
          if (group.length === 0) return null;

          return (
            <div key={kind}>
              <p className="mb-1.5 text-xs font-medium text-ink-muted">
                {KIND_LABEL[kind]}
                <span className="ml-1.5 font-normal text-ink-faint">
                  {group.length}
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                {group.map((photo) => (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => setOpenIndex(photos.indexOf(photo))}
                    className="group w-32 overflow-hidden rounded-md border border-border bg-surface text-left transition-colors hover:border-border-strong"
                  >
                    {mediaKindIsVideo(photo.kind) ? (
                      // The room scan is a real WebM clip; its poster frame
                      // stands in for a thumbnail, and the lightbox plays it.
                      <video
                        src={photo.url}
                        muted
                        playsInline
                        preload="metadata"
                        aria-label={`${KIND_LABEL[kind]} at ${offsetLabel(photo.offset_ms)}`}
                        className="aspect-4/3 w-full bg-surface-sunken object-cover"
                      />
                    ) : (
                      <img
                        src={photo.url}
                        alt={`${KIND_LABEL[kind]} at ${offsetLabel(photo.offset_ms)}`}
                        loading="lazy"
                        className="aspect-4/3 w-full object-cover"
                      />
                    )}
                    <span className="tabular block px-1.5 py-1 text-2xs text-ink-muted">
                      {offsetLabel(photo.offset_ms)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-ink/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`${KIND_LABEL[open.kind]} photo`}
          onClick={() => setOpenIndex(null)}
        >
          <div
            className="mx-auto flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-md border border-border bg-surface"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">
                  {KIND_LABEL[open.kind]}
                </p>
                <p className="tabular text-2xs text-ink-muted">
                  {offsetLabel(open.offset_ms)} · <time>{formatTime(open.created_at)}</time>
                  <span className="ml-1.5">
                    {(openIndex ?? 0) + 1} of {photos.length}
                  </span>
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {/* Pre-exam photos are seekable too: the timeline extends back
                    over the setup window, so the phone footage from the moment
                    a room scan was taken is there to watch. */}
                {onSeek ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      onSeek(open.offset_ms);
                      setOpenIndex(null);
                    }}
                  >
                    Jump to this moment
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<X />}
                  onClick={() => setOpenIndex(null)}
                  aria-label="Close"
                />
              </div>
            </div>

            <div className="flex min-h-0 flex-1 items-center gap-2 bg-surface-sunken p-3">
              <Button
                variant="ghost"
                icon={<ChevronLeft />}
                disabled={openIndex === 0}
                onClick={() => step(-1)}
                aria-label="Previous photo"
              />
              {mediaKindIsVideo(open.kind) ? (
                <video
                  src={open.url}
                  controls
                  autoPlay
                  playsInline
                  aria-label={`${KIND_LABEL[open.kind]} at ${offsetLabel(open.offset_ms)}`}
                  className={cn("mx-auto max-h-[70vh] min-h-0 object-contain")}
                />
              ) : (
                <img
                  src={open.url}
                  alt={`${KIND_LABEL[open.kind]} at ${offsetLabel(open.offset_ms)}`}
                  className={cn("mx-auto max-h-[70vh] min-h-0 object-contain")}
                />
              )}
              <Button
                variant="ghost"
                icon={<ChevronRight />}
                disabled={openIndex === photos.length - 1}
                onClick={() => step(1)}
                aria-label="Next photo"
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
